import React from 'react';
import { useNavigate } from 'react-router-dom';
import { gpuAPI } from '../../services/api';
import ActionsMenu, { MENU_ITEM } from '../../components/ActionsMenu';

// Parse the GpuRequest id from an allocation's notes ("req#<id> | ..."). resize
// targets the request, not the allocation.
function reqIdFromAlloc(a) {
  const m = /req#(\d+)/.exec(a?.notes || '');
  return m ? m[1] : null;
}

// Edit (resize) modal — change GPU count / vCPU / RAM. Recreates the pod; /app data
// is preserved. Storage is shown read-only (a bound volume can't resize in place).
function GpuEditModal({ alloc, onClose, onDone }) {
  const reqId = reqIdFromAlloc(alloc);
  const [gpuCount, setGpuCount] = React.useState(alloc?.gpu_count || 1);
  const [vcpu, setVcpu]         = React.useState('');
  const [ram, setRam]           = React.useState('');
  const [saving, setSaving]     = React.useState(false);
  const [error, setError]       = React.useState('');

  const submit = async () => {
    if (!reqId) { setError('Cannot resolve this instance (missing req id)'); return; }
    setSaving(true); setError('');
    try {
      const payload = { gpu_count: parseInt(gpuCount) || 1 };
      if (vcpu) payload.vcpu = parseInt(vcpu);
      if (ram)  payload.ram_gb = parseInt(ram);
      await gpuAPI.resizeInstance(reqId, payload);
      onDone && onDone();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Resize failed');
    } finally { setSaving(false); }
  };

  const lb  = (t) => <label style={{ fontSize:12, fontWeight:600, color: 'var(--ink-2)', display:'block', marginBottom:4 }}>{t}</label>;

  return (
    <div className="deploy-modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="deploy-modal" style={{ maxWidth: 460 }}>
        <div className="deploy-modal-header">
          <h3 style={{ margin:0 }}>Edit instance — {alloc.instance_name}</h3>
          <button className="deploy-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20, display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6, padding:'8px 10px', fontSize:12, color:'#92400e' }}>
            ⚠ Changing GPU / CPU / RAM <b>recreates the pod</b> (brief downtime). Your
            <b> /app</b> data is preserved. Anything outside /app (container filesystem) is lost.
            The SSH port/password may change.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div>{lb('GPUs')}<input type="number" min="1" max="8" value={gpuCount} onChange={e=>setGpuCount(e.target.value)} className="input" /></div>
            <div>{lb('vCPU')}<input type="number" min="1" max="192" placeholder="keep" value={vcpu} onChange={e=>setVcpu(e.target.value)} className="input" /></div>
            <div>{lb('RAM (GB)')}<input type="number" min="1" max="1900" placeholder="keep" value={ram} onChange={e=>setRam(e.target.value)} className="input" /></div>
          </div>
          <div>
            {lb('Storage')}
            <input value="Fixed after creation (data preserved)" readOnly disabled
              className="input" style={{ background: 'var(--bg)', color: 'var(--muted-2)' }} />
          </div>
          {error && <div style={{ color:'#dc2626', fontSize:12 }}>{error}</div>}
          <div className="deploy-actions">
            <button className="button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="button-primary" onClick={submit} disabled={saving}>
              {saving ? 'Applying…' : 'Apply & recreate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLOR = {
  available:   { bg: 'var(--color-success-50)', color: 'var(--color-success-500)', label: 'Available' },
  provisioned: { bg: 'var(--brand-soft)', color: 'var(--brand)', label: 'In Use' },
  maintenance: { bg: 'var(--color-warning-50)', color: 'var(--color-warning-500)', label: 'Maintenance' },
  running:     { bg: 'var(--color-success-50)', color: 'var(--color-success-500)', label: 'Running' },
  stopped:     { bg: 'var(--bg)', color: 'var(--muted)', label: 'Stopped' },
  pending:     { bg: 'var(--accent-soft)', color: 'var(--accent)', label: 'Pending' },
  terminated:  { bg: 'var(--color-danger-50)', color: 'var(--color-danger-500)', label: 'Terminated' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg: 'var(--bg)', color: 'var(--muted)', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px',
      borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{s.label}</span>
  );
}

function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).catch(() => _fallbackCopy(text));
  } else {
    _fallbackCopy(text);
  }
}
function _fallbackCopy(text) {
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try { document.execCommand('copy'); } catch (e) {}
  document.body.removeChild(el);
}

// My Instances tab — current user's GPU allocations
export function GpuInstances({ myAllocs, onStop, onStart, onTerminate, onOpenTerminal, onRefresh, canEdit, canDelete }) {
  const [editAlloc, setEditAlloc] = React.useState(null);
  const navigate = useNavigate();
  const go = (a) => navigate(`/gpu/instances/${a.id}`);
  return (
    <div>
      {editAlloc && (
        <GpuEditModal alloc={editAlloc} onClose={() => setEditAlloc(null)} onDone={onRefresh} />
      )}
      {myAllocs.length === 0 ? (
        <div className="gpu-empty">
          <div style={{ fontSize: 48 }}>🚀</div>
          <div>No active GPU instances.</div>
          <div style={{ color: 'var(--muted-2)', fontSize: 14, marginTop: 8 }}>
            Go to GPU Servers tab and click Allocate on any available instance.
          </div>
        </div>
      ) : (
        <table className="ds">
          <thead>
            <tr>
              {['Instance','GPU','VRAM','Status','SSH','Age',''].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {myAllocs.map(a => (
              <tr key={a.id} onClick={() => go(a)} style={{ cursor:'pointer' }}>
                <td style={{ fontWeight:600 }}>{a.instance_name}</td>
                <td style={{ color: 'var(--muted)' }}>{a.gpu_model}</td>
                <td>{a.vram_gb}GB</td>
                <td><StatusBadge status={a.status} /></td>
                <td style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{a.ssh_port ? `:${a.ssh_port}` : '—'}</td>
                <td style={{ color: 'var(--muted-2)' }}>{a.gpu_hours_used}h</td>
                <td onClick={e=>e.stopPropagation()}>
                  <ActionsMenu>
                    <button style={MENU_ITEM} onClick={() => go(a)}>Open</button>
                    {a.status==='running' && canEdit && (
                      <button style={MENU_ITEM} onClick={() => setEditAlloc(a)}>Edit</button>
                    )}
                    {canDelete && (
                    <button style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}
                      onClick={() => onTerminate(a.id)}>Terminate</button>
                    )}
                  </ActionsMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Admin All Allocations tab
export function GpuAllocations({ allAllocs, onTerminate }) {
  return (
    <div className="table-card">
      <table className="ds">
        <thead>
          <tr>
            <th>User</th><th>Instance</th><th>GPU</th><th>VRAM</th>
            <th>SSH Port</th><th>Status</th><th>Hours Used</th><th>Allocated</th><th></th>
          </tr>
        </thead>
        <tbody>
          {allAllocs.length === 0 ? (
            <tr><td colSpan={9} style={{ textAlign:'center', color: 'var(--muted)' }}>No allocations</td></tr>
          ) : allAllocs.map(a => (
            <tr key={a.id}>
              <td>{a.username}</td>
              <td><code>{a.instance_name}</code></td>
              <td>{a.gpu_model}</td>
              <td>{a.vram_gb}GB</td>
              <td style={{ fontFamily: 'monospace' }}>{a.ssh_port || '—'}</td>
              <td><StatusBadge status={a.status} /></td>
              <td style={{ color: 'var(--muted)' }}>{a.gpu_hours_used}h</td>
              <td style={{ color: 'var(--muted-2)' }}>
                {a.allocated_at ? new Date(a.allocated_at).toLocaleDateString() : '—'}
              </td>
              <td>
                {['running','pending','stopped'].includes(a.status) && (
                  <button className="btn btn-tinted-danger btn-sm"
                    onClick={() => onTerminate(a.id)}>Terminate</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
